import React, { useState, useEffect } from "react";
import { Box, Button, Flex, HStack, Icon, Input, Switch, Text, Textarea, Tooltip } from "@chakra-ui/react";
import { AppIcons } from "constants/icons";
import cronstrue from "cronstrue";
import { ScheduledJobProto } from "build-ts/schedule";
import { ArtifactProto } from "build-ts/chat_session";
import WorkflowSectionHeading from "modules/analyst/components/WorkflowSectionHeading";
import { getTooltip } from "utils/getMessages";
import { MarkdownRenderer } from "modules/common/MarkdownRenderer";
import ArtifactPanelHeader from "../ArtifactPanelHeader";
import WorkflowRunsOutputListContainer from "modules/workflow/containers/WorkflowRunsOutputList";
import WorkflowRunSearchCardContainer from "./containers/WorkflowRunSearchCard";
import { HeaderWithStats } from "services/MetadataServices";
import { putScheduledJob } from "services/ScheduleServices";
import showToast from "hooks/useCustomToast";

type TabId = "analysis" | "past_runs" | "search_results";

interface WorkflowCardProps {
  /** SCHEDULED_JOB artifact (from reasoning message) */
  scheduleJob?: ScheduledJobProto | null;
  /** Whether this job is already saved */
  isSavedInAnalyst: boolean;
  /** Whether the artifact panel is currently expanded to full width */
  isExpanded?: boolean;
  /** Called when the expand/collapse toggle button is clicked */
  onExpandToggle?: () => void;
  /** Restore to the tab the user was on when they last left this artifact */
  initialActiveTab?: string;
  /** Called when the user changes the active tab so the parent can persist it */
  onViewStateChange?: (view: { activeTab: string }) => void;
  /** Latest WORKFLOW_RUN_SEARCH artifact matching this workflow's job_id */
  searchArtifact?: ArtifactProto;
  /** Tab the parent wants WorkflowCard to switch to: "analysis" | "past_runs" | "search_results" */
  requestedActiveTab?: string;
  /** User's preferred timezone, forwarded to WorkflowRunSearchCardContainer */
  defaultTimeZone?: string;
  /** Analyst list, forwarded to WorkflowRunSearchCardContainer */
  analystList?: HeaderWithStats[];
  /** Scenario jobs, forwarded to WorkflowRunSearchCardContainer */
  scenarioJobs?: ScheduledJobProto[];
  /** Called when user opens a workflow run from the Search Results tab */
  onOpenWorkflowRun?: (artifact: ArtifactProto) => void;
  // Called immediately after a successful PUT so the parent can update its artifact panel
  // with the freshly-saved job, preventing a stale-prop flicker in the display.
  onJobSaved?: (savedJob: ScheduledJobProto) => void;
  // Called with the saved job after a successful PUT so the parent can rebuild Darwin's
  // in-memory system prompt before the follow-up message is sent to the LLM.
  onRefreshSystemPrompt?: (savedJob: ScheduledJobProto) => Promise<void>;
}

const WorkflowCard: React.FC<WorkflowCardProps> = ({
  scheduleJob: initialScheduleJob,
  isSavedInAnalyst,
  isExpanded,
  onExpandToggle,
  initialActiveTab,
  onViewStateChange,
  searchArtifact,
  requestedActiveTab,
  defaultTimeZone,
  analystList,
  scenarioJobs,
  onOpenWorkflowRun,
  onJobSaved,
  onRefreshSystemPrompt,
}) => {
  const validTabs: TabId[] = ["analysis", "past_runs", "search_results"];
  const isValidWorkflowTab = (tab?: string): tab is TabId => !!tab && validTabs.includes(tab as TabId);

  const [activeTab, setActiveTab] = useState<TabId>(
    isValidWorkflowTab(initialActiveTab) ? initialActiveTab : "analysis"
  );

  const [jobName, setJobName] = useState(initialScheduleJob?.header?.name ?? "");
  const [trigger, setTrigger] = useState(initialScheduleJob?.scheduled_analysis?.trigger ?? "");
  const [reportingInstructions, setReportingInstructions] = useState(
    initialScheduleJob?.scheduled_analysis?.reporting_instructions ?? ""
  );
  const [cronExpression, setCronExpression] = useState(initialScheduleJob?.cron_expr ?? "");
  const [isScheduleActive, setIsScheduleActive] = useState(initialScheduleJob?.is_active ?? false);
  const [showCronRaw, setShowCronRaw] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Draft state — only written when the user clicks Edit, committed on Save
  const [draftName, setDraftName] = useState("");
  const [draftTrigger, setDraftTrigger] = useState("");
  const [draftCron, setDraftCron] = useState("");
  const [draftActive, setDraftActive] = useState(false);
  const [draftReportingInstructions, setDraftReportingInstructions] = useState("");

  useEffect(() => {
    if (initialScheduleJob) {
      setJobName(initialScheduleJob.header?.name ?? "");
      setTrigger(initialScheduleJob.scheduled_analysis?.trigger ?? "");
      setReportingInstructions(initialScheduleJob.scheduled_analysis?.reporting_instructions ?? "");
      setCronExpression(initialScheduleJob.cron_expr ?? "");
      setIsScheduleActive(initialScheduleJob.is_active ?? false);
    } else {
      setJobName("");
      setTrigger("");
      setReportingInstructions("");
      setCronExpression("");
      setIsScheduleActive(false);
    }
    // Discard any in-flight edits when the underlying job changes
    setIsEditing(false);
  }, [initialScheduleJob]);

  // Switch to the tab requested by the parent (e.g. "search_results" when a search artifact is
  // clicked, "analysis" when the workflow itself is clicked directly).
  useEffect(() => {
    if (requestedActiveTab && isValidWorkflowTab(requestedActiveTab)) {
      setActiveTab(requestedActiveTab);
    }
  }, [requestedActiveTab]);

  const cronHumanReadable = (() => {
    if (!cronExpression.trim()) return null;
    try {
      return cronstrue.toString(cronExpression.trim());
    } catch {
      return null;
    }
  })();

  const draftCronHumanReadable = (() => {
    if (!draftCron.trim()) return null;
    try {
      return cronstrue.toString(draftCron.trim());
    } catch {
      return null;
    }
  })();

  const displayLabel = jobName?.trim() || initialScheduleJob?.header?.name?.trim() || "New Job";

  const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
    { id: "analysis", label: "Workflow", icon: AppIcons.workflow },
    { id: "past_runs", label: "Past Runs", icon: AppIcons.workflowRun },
    ...(searchArtifact ? [{ id: "search_results" as TabId, label: "Search Results", icon: AppIcons.search }] : []),
  ];

  const handleEditClick = () => {
    setDraftName(jobName);
    setDraftTrigger(trigger);
    setDraftCron(cronExpression);
    setDraftActive(isScheduleActive);
    setDraftReportingInstructions(reportingInstructions);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleSave = async () => {
    // If the job is already persisted, save the edited fields via API first so
    // the agent sees the up-to-date workflow when it fetches details.
    if (initialScheduleJob?.header?.id) {
      setIsSaving(true);
      try {
        const job = ScheduledJobProto.create({
          ...initialScheduleJob,
          header: {
            ...initialScheduleJob.header,
            name: draftName.trim() || (initialScheduleJob.header.name ?? ""),
            description: draftTrigger || (initialScheduleJob.header.description ?? ""),
          },
          cron_expr: draftCron,
          is_active: draftActive,
          scheduled_analysis: {
            ...initialScheduleJob.scheduled_analysis,
            trigger: draftTrigger,
            reporting_instructions: draftReportingInstructions,
          },
        });
        const savedJob = await putScheduledJob(job);
        if (!savedJob) {
          showToast("Failed to save workflow", "", "error");
          return;
        }

        // Update the artifact panel immediately so initialScheduleJob reflects the
        // saved values before onRefreshSystemPrompt is called. Without this, a reference
        // change on initialScheduleJob can trigger the sync useEffect with the old
        // prop value, causing a brief display flicker back to pre-edit content.
        onJobSaved?.(savedJob);

        // Rebuild Darwin's in-memory system prompt so the LLM sees the updated
        // workflow name/description from the very first token of the next turn.
        await onRefreshSystemPrompt?.(savedJob);
      } catch {
        showToast("Failed to save workflow", "", "error");
        return;
      } finally {
        setIsSaving(false);
      }
    }

    // Commit drafts to local display state
    setJobName(draftName);
    setTrigger(draftTrigger);
    setCronExpression(draftCron);
    setIsScheduleActive(draftActive);
    setReportingInstructions(draftReportingInstructions);
    setIsEditing(false);
  };

  // SAVED/UNSAVED status badge — shown in the header actions area
  const savedDraftBadge = isSavedInAnalyst ? (
    <Text
      fontSize="2xs"
      color="green.800"
      bg="green.100"
      px={2}
      py={0.5}
      borderRadius="sm"
      fontWeight="700"
      flexShrink={0}
      mb={0}
      border="1px solid"
      borderColor="green.200"
    >
      SAVED
    </Text>
  ) : (
    <Tooltip label="Please ask the analyst to save this workflow" hasArrow placement="top">
      <Text
        fontSize="2xs"
        color="orange.700"
        bg="yellow.100"
        px={2}
        py={0.5}
        borderRadius="sm"
        fontWeight="700"
        flexShrink={0}
        mb={0}
        border="1px solid"
        borderColor="yellow.500"
      >
        UNSAVED CHANGES
      </Text>
    </Tooltip>
  );

  // Header action area: Cancel+Save buttons in edit mode, Edit+badge otherwise
  const headerActions = isEditing ? (
    <HStack spacing={1} flexShrink={0}>
      <Button size="xs" variant="ghost" color="gray.500" onClick={handleCancelEdit} isDisabled={isSaving} h="1.4rem">
        Cancel
      </Button>
      <Button
        size="xs"
        colorScheme="purple"
        leftIcon={<Icon as={AppIcons.save} boxSize={3} />}
        onClick={handleSave}
        isLoading={isSaving}
        loadingText="Saving…"
        h="1.4rem"
      >
        Save
      </Button>
    </HStack>
  ) : (
    <HStack spacing={1} flexShrink={0}>
      <Button
        size="xs"
        variant="ghost"
        color="purple.600"
        _hover={{ bg: "purple.50" }}
        leftIcon={<Icon as={AppIcons.edit} boxSize={3} />}
        onClick={handleEditClick}
        flexShrink={0}
        h="1.4rem"
      >
        Edit
      </Button>
      {savedDraftBadge}
    </HStack>
  );

  return (
    <Box
      position="relative"
      width="100%"
      h="100%"
      minH={0}
      bg="white"
      display="flex"
      flexDirection="column"
      overflow="hidden"
    >
      <ArtifactPanelHeader
        icon={AppIcons.workflow}
        iconBg="white"
        iconColor="purple.700"
        name={displayLabel}
        typeLabel="Workflow"
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={id => {
          const tab = id as TabId;
          setActiveTab(tab);
          onViewStateChange?.({ activeTab: tab });
        }}
        actions={headerActions}
        isExpanded={isExpanded}
        onExpandToggle={onExpandToggle}
      />

      <Box
        flex={1}
        minH={0}
        display="flex"
        flexDirection="column"
        overflowY={activeTab === "analysis" ? "hidden" : "auto"}
        sx={
          activeTab === "analysis"
            ? undefined
            : {
                "&::-webkit-scrollbar": { width: "4px" },
                "&::-webkit-scrollbar-track": { background: "transparent" },
                "&::-webkit-scrollbar-thumb": {
                  background: "var(--chakra-colors-gray-300)",
                  borderRadius: "2px",
                },
              }
        }
      >
        {/* ── Workflow tab ─────────────────────────────────────────────────── */}
        {activeTab === "analysis" && (
          <Flex direction="column" flex="1" minH={0} minW={0} w="100%" px={2} py={2} gap={2.5}>
            {/* Name */}
            <Box display="flex" flexDirection="column" minW={0} flexShrink={0}>
              <WorkflowSectionHeading label="Name" compact />
              {isEditing ? (
                <Input
                  size="sm"
                  value={draftName}
                  onChange={e => setDraftName(e.target.value)}
                  placeholder="Workflow name"
                  ml={2.5}
                  borderColor="gray.300"
                  _focus={{
                    borderColor: "purple.400",
                    boxShadow: "0 0 0 1px var(--chakra-colors-purple-400)",
                  }}
                  fontSize="0.8rem"
                />
              ) : (
                <Input
                  size="sm"
                  value={jobName.trim() || "No name set."}
                  isReadOnly
                  ml={2.5}
                  borderColor="transparent"
                  fontSize="0.8rem"
                  fontWeight="500"
                  color={jobName.trim() ? "gray.700" : "gray.400"}
                  cursor="default"
                  _hover={{ borderColor: "transparent" }}
                  _focus={{ boxShadow: "none", borderColor: "transparent" }}
                />
              )}
            </Box>

            {/* Description */}
            <Box display="flex" flexDirection="column" minW={0} flexShrink={0}>
              <WorkflowSectionHeading
                label="Description"
                compact
                tooltip={getTooltip("workflow_detail.description.tooltip")}
              />
              {isEditing ? (
                <Textarea
                  value={draftTrigger}
                  onChange={e => setDraftTrigger(e.target.value)}
                  placeholder="Describe what triggers this workflow"
                  ml={2.5}
                  h="7.5rem"
                  minH="7.5rem"
                  maxH="7.5rem"
                  px={3}
                  py={2}
                  borderColor="gray.300"
                  borderWidth="1px"
                  borderRadius="md"
                  _focus={{
                    borderColor: "purple.400",
                    boxShadow: "0 0 0 1px var(--chakra-colors-purple-400)",
                  }}
                  fontSize="sm"
                  resize="none"
                />
              ) : (
                <Box
                  ml={2.5}
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  px={3}
                  pt={0}
                  pb={2}
                  bg="white"
                  h="7.5rem"
                  overflowY="auto"
                  sx={{
                    "&::-webkit-scrollbar": { width: "4px" },
                    "&::-webkit-scrollbar-track": { background: "transparent" },
                    "&::-webkit-scrollbar-thumb": {
                      background: "var(--chakra-colors-gray-300)",
                      borderRadius: "2px",
                    },
                  }}
                >
                  {trigger.trim() ? (
                    <MarkdownRenderer content={trigger} isArtifactsPanelVisible={true} />
                  ) : (
                    <Text fontSize="sm" fontWeight="400" color="gray.400" mt={2} mb={0}>
                      No description set.
                    </Text>
                  )}
                </Box>
              )}
            </Box>

            {/* Run frequency + Status */}
            <Flex gap={2} align="flex-start" w="100%" flexShrink={0}>
              <Box flex="3" minW={0}>
                <WorkflowSectionHeading
                  label="Run frequency"
                  compact
                  tooltip={getTooltip("workflow_detail.schedule.tooltip")}
                  labelTrailing={
                    !isEditing && cronExpression.trim() ? (
                      <Tooltip
                        label={showCronRaw ? "Show human readable" : "Show cron expression"}
                        placement="top"
                        hasArrow
                        fontSize="xs"
                      >
                        <Box
                          as="button"
                          type="button"
                          onClick={() => setShowCronRaw(v => !v)}
                          display="inline-flex"
                          alignItems="center"
                          justifyContent="center"
                          w="1.1rem"
                          h="1.1rem"
                          borderRadius="sm"
                          bg={showCronRaw ? "purple.500" : "transparent"}
                          color={showCronRaw ? "white" : "gray.800"}
                          _hover={{
                            bg: showCronRaw ? "purple.600" : "gray.200",
                          }}
                          flexShrink={0}
                        >
                          <Icon as={AppIcons.workflowRun} boxSize={3.5} />
                        </Box>
                      </Tooltip>
                    ) : undefined
                  }
                />
                {isEditing ? (
                  <Box ml={2.5}>
                    <Tooltip
                      label={draftCronHumanReadable || undefined}
                      placement="bottom-start"
                      hasArrow
                      fontSize="xs"
                      isDisabled={!draftCronHumanReadable}
                    >
                      <Input
                        size="sm"
                        value={draftCron}
                        onChange={e => setDraftCron(e.target.value)}
                        placeholder="Cron expression (e.g. 0 9 * * 1)"
                        borderColor="gray.300"
                        _focus={{
                          borderColor: "purple.400",
                          boxShadow: "0 0 0 1px var(--chakra-colors-purple-400)",
                        }}
                        fontSize="0.8rem"
                        fontFamily="mono"
                      />
                    </Tooltip>
                  </Box>
                ) : (
                  <Box ml={2.5}>
                    <Box h="2rem" display="flex" alignItems="center">
                      <Text
                        fontSize="0.8rem"
                        fontWeight="500"
                        color={cronExpression.trim() ? "gray.700" : "gray.400"}
                        fontFamily={showCronRaw && cronExpression.trim() ? "mono" : "body"}
                        mb={0}
                        noOfLines={1}
                      >
                        {cronExpression.trim()
                          ? showCronRaw
                            ? cronExpression.trim()
                            : (cronHumanReadable ?? cronExpression.trim())
                          : "No schedule set."}
                      </Text>
                    </Box>
                  </Box>
                )}
              </Box>
              <Box flex="1" minW={0}>
                <WorkflowSectionHeading label="Status" compact />
                {isEditing ? (
                  <Box pl={2.5} h="2rem" display="flex" alignItems="center">
                    <HStack spacing={2} align="center">
                      <Switch
                        size="sm"
                        isChecked={draftActive}
                        onChange={e => setDraftActive(e.target.checked)}
                        colorScheme="green"
                        mb={0}
                      />
                      <Text
                        fontSize="xs"
                        fontWeight="600"
                        color={draftActive ? "green.700" : "gray.500"}
                        mb={0}
                        lineHeight={1}
                      >
                        {draftActive ? "Live" : "Paused"}
                      </Text>
                    </HStack>
                  </Box>
                ) : (
                  <Box pl={2.5} h="2rem" display="flex" alignItems="center">
                    <HStack
                      spacing={1.5}
                      align="center"
                      bg={isScheduleActive ? "green.50" : "gray.100"}
                      px={2.5}
                      py={1}
                      w="fit-content"
                    >
                      {isScheduleActive ? (
                        <Box w="6px" h="6px" borderRadius="full" bg="green.400" flexShrink={0} />
                      ) : (
                        <Icon as={AppIcons.pause} boxSize={2} color="gray.400" />
                      )}
                      <Text
                        as="span"
                        fontSize="2xs"
                        fontWeight="700"
                        letterSpacing="0.06em"
                        textTransform="uppercase"
                        color={isScheduleActive ? "green.700" : "gray.500"}
                        lineHeight={1}
                      >
                        {isScheduleActive ? "Live" : "Paused"}
                      </Text>
                    </HStack>
                  </Box>
                )}
              </Box>
            </Flex>

            {/* Instructions */}
            <Box display="flex" flexDirection="column" flex="1" minH={0} minW={0} w="100%">
              <WorkflowSectionHeading
                label="Instructions"
                compact
                tooltip={getTooltip("workflow_detail.instructions.tooltip")}
              />
              {isEditing ? (
                <Textarea
                  size="sm"
                  value={draftReportingInstructions}
                  onChange={e => setDraftReportingInstructions(e.target.value)}
                  placeholder="Reporting instructions for this workflow"
                  ml={2.5}
                  flex="1"
                  minH={0}
                  h="100%"
                  borderColor="gray.300"
                  _focus={{
                    borderColor: "purple.400",
                    boxShadow: "0 0 0 1px var(--chakra-colors-purple-400)",
                  }}
                  fontSize="sm"
                  resize="none"
                />
              ) : (
                <Box
                  ml={2.5}
                  flex="1"
                  minH={0}
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  px={3}
                  py={0}
                  bg="white"
                  overflowY="auto"
                  overflowX="auto"
                  sx={{
                    "&::-webkit-scrollbar": { width: "4px" },
                    "&::-webkit-scrollbar-track": { background: "transparent" },
                    "&::-webkit-scrollbar-thumb": {
                      background: "var(--chakra-colors-gray-300)",
                      borderRadius: "2px",
                    },
                  }}
                >
                  {reportingInstructions.trim() ? (
                    <MarkdownRenderer content={reportingInstructions} isArtifactsPanelVisible={true} />
                  ) : (
                    <Text fontSize="sm" fontWeight="400" color="gray.400" mb={0}>
                      No instructions set.
                    </Text>
                  )}
                </Box>
              )}
            </Box>
          </Flex>
        )}

        {/* ── Past Runs tab ─────────────────────────────────────────────────── */}
        {activeTab === "past_runs" && (
          <Flex direction="column" flex="1" minH={0} w="100%" overflow="hidden" px={2} py={2}>
            <WorkflowRunsOutputListContainer
              selectedJob={initialScheduleJob?.header}
              isTabActive={activeTab === "past_runs"}
            />
          </Flex>
        )}

        {/* ── Search Results tab ───────────────────────────────────────────── */}
        {activeTab === "search_results" && (
          <Box flex={1} minH={0} display="flex" flexDirection="column" overflow="hidden">
            {searchArtifact ? (
              <WorkflowRunSearchCardContainer
                artifact={searchArtifact}
                defaultTimeZone={defaultTimeZone}
                onOpenWorkflowRun={onOpenWorkflowRun}
                analystList={analystList}
                scenarioJobs={scenarioJobs}
              />
            ) : (
              <Flex align="center" justify="center" flex={1} p={6}>
                <Text fontSize="sm" color="gray.400" fontStyle="italic" mb={0}>
                  No search results yet.
                </Text>
              </Flex>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default WorkflowCard;
